import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { TransactionHistory, PaymentSummaryCard, REFUND_REASONS } from "./TransactionHistory";
import { X, Plus, Undo2, Loader2, CreditCard, Banknote, AlertTriangle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Add Payment Modal - Clean style matching existing modals
function AddPaymentModal({ bookingId, token, onClose, onSuccess }) {
  const [type, setType] = useState("card_pos");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseInt(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/bookings/${bookingId}/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          type,
          amount: parseInt(amount),
          note: note || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to add payment");
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-lg">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-bold">Add Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-gray-500 mb-2">Payment Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType("card_pos")}
                className={`p-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 ${
                  type === "card_pos" 
                    ? "border-teal-400 bg-teal-50 text-teal-700" 
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <CreditCard className="h-4 w-4" />
                Card (POS)
              </button>
              <button
                type="button"
                onClick={() => setType("cash")}
                className={`p-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 ${
                  type === "cash" 
                    ? "border-teal-400 bg-teal-50 text-teal-700" 
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <Banknote className="h-4 w-4" />
                Cash
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Amount (€)</label>
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              placeholder="0"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              placeholder="e.g., Paid to driver"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={loading} className="flex-1 bg-teal-500 hover:bg-teal-600">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Add Payment
            </Button>
            <Button type="button" onClick={onClose} variant="outline">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add Refund Modal
function AddRefundModal({ bookingId, maxAmount, token, onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("price_adjustment");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const refundAmount = parseInt(amount);
    
    if (!amount || refundAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    
    if (refundAmount > maxAmount) {
      setError(`Refund cannot exceed €${maxAmount}`);
      return;
    }

    if (reason === "other" && !note) {
      setError("Please provide a note for 'Other' reason");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/bookings/${bookingId}/refund`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: refundAmount,
          reason,
          note: note || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to process refund");
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-lg">
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h2 className="text-lg font-bold">Process Refund</h2>
            <p className="text-sm text-gray-500">Max: €{maxAmount}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Amount (€)</label>
            <input
              type="number"
              min="1"
              max={maxAmount}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              placeholder="0"
              required
            />
            <div className="flex gap-2 mt-2">
              {[25, 50, 100].map(pct => {
                const val = Math.round(maxAmount * pct / 100);
                return (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setAmount(val.toString())}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    {pct}% (€{val})
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
            >
              {Object.entries(REFUND_REASONS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">
              Note {reason === "other" && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              placeholder="Additional details..."
              required={reason === "other"}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={loading} className="flex-1 bg-teal-500 hover:bg-teal-600">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Process Refund
            </Button>
            <Button type="button" onClick={onClose} variant="outline">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Transaction Modal
function EditTransactionModal({ transaction, token, onClose, onSuccess }) {
  const [amount, setAmount] = useState(transaction.amount.toString());
  const [note, setNote] = useState(transaction.note || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseInt(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/bookings/transactions/${transaction.transaction_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseInt(amount),
          note: note || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to update");
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-sm shadow-lg">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-bold">Edit Transaction</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Amount (€)</label>
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" disabled={loading} className="flex-1 bg-teal-500 hover:bg-teal-600">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
            <Button type="button" onClick={onClose} variant="outline">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Payment Management Panel
export default function PaymentManagementPanel({ booking, token, onUpdate }) {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddRefund, setShowAddRefund] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [txnRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE}/api/bookings/${booking.booking_id}/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/bookings/${booking.booking_id}/payment-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (txnRes.ok) setTransactions(await txnRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (err) {
      console.error("Failed to fetch payment data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (booking?.booking_id) fetchData();
  }, [booking?.booking_id]);

  const handleSuccess = () => {
    setShowAddPayment(false);
    setShowAddRefund(false);
    setEditingTransaction(null);
    fetchData();
    if (onUpdate) onUpdate();
  };

  const handleDelete = async (transaction) => {
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/bookings/transactions/${transaction.transaction_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to delete");
      }

      setDeletingTransaction(null);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Payment Summary */}
      <PaymentSummaryCard summary={summary} />

      {/* Refund Alert for Cancelled Bookings */}
      {booking?.booking_status === "cancelled" && summary?.net_paid > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-orange-800">Refund Required</p>
            <p className="text-xs text-orange-700">Customer paid €{summary.net_paid}. Process refund.</p>
          </div>
          <Button
            onClick={() => setShowAddRefund(true)}
            size="sm"
            className="bg-teal-500 hover:bg-teal-600 text-xs"
          >
            Refund €{summary.net_paid}
          </Button>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button onClick={() => setShowAddPayment(true)} size="sm" className="bg-teal-500 hover:bg-teal-600">
          <Plus className="h-4 w-4 mr-1" /> Add Payment
        </Button>
        <Button
          onClick={() => setShowAddRefund(true)}
          size="sm"
          variant="outline"
          disabled={!summary || summary.net_paid <= 0}
        >
          <Undo2 className="h-4 w-4 mr-1" /> Add Refund
        </Button>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-4 py-3 border-b">
          <h3 className="font-medium text-gray-900 text-sm">Transaction History</h3>
        </div>
        <div className="px-4">
          <TransactionHistory
            transactions={transactions}
            isAdmin={true}
            onEdit={(txn) => setEditingTransaction(txn)}
            onDelete={(txn) => setDeletingTransaction(txn)}
            emptyMessage="No transactions recorded"
          />
        </div>
      </div>

      {/* Modals */}
      {showAddPayment && (
        <AddPaymentModal
          bookingId={booking.booking_id}
          token={token}
          onClose={() => setShowAddPayment(false)}
          onSuccess={handleSuccess}
        />
      )}

      {showAddRefund && summary && (
        <AddRefundModal
          bookingId={booking.booking_id}
          maxAmount={summary.net_paid}
          token={token}
          onClose={() => setShowAddRefund(false)}
          onSuccess={handleSuccess}
        />
      )}

      {editingTransaction && (
        <EditTransactionModal
          transaction={editingTransaction}
          token={token}
          onClose={() => setEditingTransaction(null)}
          onSuccess={handleSuccess}
        />
      )}

      {/* Delete Confirmation */}
      {deletingTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-4 shadow-lg">
            <h3 className="font-bold mb-2">Delete Transaction?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Delete €{deletingTransaction.amount} {deletingTransaction.type} transaction?
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => handleDelete(deletingTransaction)}
                disabled={deleteLoading}
                className="flex-1 bg-red-500 hover:bg-red-600"
              >
                {deleteLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Delete
              </Button>
              <Button onClick={() => setDeletingTransaction(null)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { AddPaymentModal, AddRefundModal };
